import React, { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  buildTwitchPlayerUrl,
  resolveTwitchPlayerParentDomains,
  type TwitchPlayerMode,
} from '../twitchEmbed';

export type TwitchPlayerProps = {
  mode: TwitchPlayerMode;
  videoId?: string | null;
  channelLogin?: string | null;
  title?: string;
  autoplay?: boolean;
  muted?: boolean;
  startTimeSeconds?: number;
  className?: string;
  fallbackUrl?: string | null;
  thumbnailUrl?: string | null;
  embedParentDomains?: string[] | null;
  /** Override default VOD unavailable copy (archive watch surfaces). */
  vodUnavailableCopy?: string | null;
  /** When false, hides the footer Open on Twitch link (parent surfaces may provide their own). */
  showExternalLink?: boolean;
};

const VOD_FALLBACK_COPY =
  'This Twitch VOD may be unavailable or expired. Open on Twitch to confirm.';

const LIVE_FALLBACK_COPY =
  'This Twitch stream cannot be embedded yet. Open on Twitch to confirm.';

const TwitchPlayer: React.FC<TwitchPlayerProps> = ({
  mode,
  videoId,
  channelLogin,
  title,
  autoplay = false,
  muted,
  startTimeSeconds,
  className = '',
  fallbackUrl,
  thumbnailUrl,
  embedParentDomains,
  vodUnavailableCopy,
  showExternalLink = true,
}) => {
  const playerUrl = useMemo(
    () =>
      buildTwitchPlayerUrl({
        mode,
        videoId,
        channelLogin,
        parentDomains: resolveTwitchPlayerParentDomains(embedParentDomains),
        autoplay,
        muted,
        startTimeSeconds,
      }),
    [
      mode,
      videoId,
      channelLogin,
      embedParentDomains,
      autoplay,
      muted,
      startTimeSeconds,
    ]
  );

  const iframeTitle =
    title != null && title.trim()
      ? mode === 'live'
        ? `Twitch live stream: ${title.trim()}`
        : `Twitch VOD: ${title.trim()}`
      : mode === 'live'
        ? 'Twitch live stream player'
        : 'Twitch VOD player';

  const openOnTwitchLink =
    fallbackUrl != null && fallbackUrl.trim() ? (
      <a
        href={fallbackUrl.trim()}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-primary-300 hover:text-primary-200 text-sm"
        data-testid="open-on-twitch-link"
      >
        <ExternalLink className="h-4 w-4" aria-hidden />
        Open on Twitch
      </a>
    ) : null;

  if (!playerUrl) {
    return (
      <div
        className={`space-y-4 ${className}`.trim()}
        data-testid="twitch-player"
        data-player-mode={mode}
      >
        <div
          className="rounded-lg border border-secondary-700 bg-secondary-800/50 p-6 text-center"
          data-testid="twitch-player-fallback"
        >
          {thumbnailUrl ? (
            <div className="relative mx-auto mb-4 aspect-video max-w-xl overflow-hidden rounded-lg border border-secondary-700 bg-black">
              <img
                src={thumbnailUrl}
                alt=""
                className="h-full w-full object-cover opacity-80"
              />
            </div>
          ) : null}
          <p className="text-secondary-300 text-sm">
            {mode === 'live'
              ? LIVE_FALLBACK_COPY
              : vodUnavailableCopy?.trim() || VOD_FALLBACK_COPY}
          </p>
          {showExternalLink && openOnTwitchLink && (
            <div className="mt-4">{openOnTwitchLink}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`space-y-4 ${className}`.trim()}
      data-testid="twitch-player"
      data-player-mode={mode}
    >
      <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-secondary-700 bg-black">
        <iframe
          src={playerUrl}
          title={iframeTitle}
          allowFullScreen
          allow="autoplay; fullscreen; picture-in-picture"
          className="absolute inset-0 h-full w-full"
          data-testid="twitch-player-iframe"
        />
      </div>
      {showExternalLink && openOnTwitchLink && (
        <div className="flex flex-wrap items-center gap-4">{openOnTwitchLink}</div>
      )}
    </div>
  );
};

export default TwitchPlayer;
